import { type DeviceItem, useLocaleStore } from '@/stores'
import {
  readMouseRtConfigSnapshot,
  type MouseRtConfigSnapshot,
  type MouseRTCapability,
  type RtVibrationFeedbackType,
} from '@rapoo/device-sdk'
import { computed, ref } from 'vue'

export function useRtData(itemRef: { value: DeviceItem }) {
  const { t } = useLocaleStore()
  const device = computed(() => itemRef.value.original)
  const snapshot = ref<MouseRtConfigSnapshot>()
  let requestId = 0

  const rtCapability = computed(() =>
    device.value.manifest.capabilities.find(
      (capability): capability is MouseRTCapability => capability.type === 'mouse-rt',
    ),
  )

  const initRtData = async () => {
    const currentRequestId = ++requestId
    snapshot.value = undefined

    const currentDevice = device.value
    const feature = currentDevice.features.mouseCalibration
    if (!feature || rtCapability.value?.config.enable !== true) return

    const data = await readMouseRtConfigSnapshot(feature, currentDevice.currentConfig.index)
    if (currentRequestId === requestId && currentDevice === device.value) {
      snapshot.value = data
    }
  }

  const formatSides = (left: string | number, right: string | number) =>
    `${t('home.overview.rtData.leftValue', { value: left })}/${t(
      'home.overview.rtData.rightValue',
      { value: right },
    )}`

  const formatRtLevel = (side: 'left' | 'right') => {
    const data = snapshot.value
    return data?.rapidTrigger.rtSwitch[side]
      ? data.rapidTrigger.rtLevel[side]
      : t('home.overview.rtData.disabledValue')
  }

  const feedbackTypeKeys: Record<RtVibrationFeedbackType, string> = {
    0: 'home.overview.rtData.mechanicalSwitch',
    1: 'home.overview.rtData.opticalSwitch',
    2: 'home.overview.rtData.electromagneticSwitch',
  }

  const rtData = computed(() => {
    const data = snapshot.value
    if (!data) return null

    const sound = (value: number) => `(${t('rt.sound.option', { value })})`
    const volume = (value: number) => `${value}%`
    const feedbackType = t(feedbackTypeKeys[data.vibration.feedbackType])

    return {
      title: t('home.overview.rtData.title'),
      data: [
        {
          name: t('home.overview.rtData.triggerTravel'),
          value: formatSides(data.travel.triggerPress.left, data.travel.triggerPress.right),
        },
        {
          name: t('home.overview.rtData.rapidTrigger'),
          value: formatSides(formatRtLevel('left'), formatRtLevel('right')),
        },
        {
          name: t('home.overview.rtData.vibration'),
          value: t('home.overview.rtData.feedbackValue', {
            type: feedbackType,
            value: formatSides(
              data.vibration.vibrationPress.left,
              data.vibration.vibrationPress.right,
            ),
          }),
        },
        {
          name: t('home.overview.rtData.sound'),
          value: formatSides(sound(data.sound.beepPress.left), sound(data.sound.beepPress.right)),
        },
        {
          name: t('home.overview.rtData.volume'),
          value: formatSides(
            volume(data.sound.beepVolume.left),
            volume(data.sound.beepVolume.right),
          ),
        },
      ],
    }
  })

  return { rtData, initRtData }
}
